import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Alert,
  List,
  ListItem,
  Chip,
  IconButton
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { setUserRole, USER_ROLES } from "../utils/userRoles";
import NavBarAdm from "./navbarAdm";

export const AdminPanel = () => {
  const navigate = useNavigate();
  // remove global add-by-id/email field — per-user inline editing instead
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingRole, setEditingRole] = useState("user");

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [allUsers, setAllUsers] = useState([]);

  // Load all users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersCollection = collection(db, "users");
        const userSnapshot = await getDocs(usersCollection);
        const usersList = userSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAllUsers(usersList);
      } catch (error) {
        console.error("Error loading users:", error);
      }
    };

    loadUsers();
  }, []);

  const refreshUsers = async () => {
    try {
      const usersCollection = collection(db, "users");
      const userSnapshot = await getDocs(usersCollection);
      const usersList = userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllUsers(usersList);
    } catch (err) {
      console.error("Error reloading users:", err);
    }
  };

  const handleSaveRole = async (userId) => {
    try {
      const success = await setUserRole(userId, editingRole);
      if (success) {
        setMessage(`Role updated to ${editingRole}`);
        setMessageType("success");
        setEditingUserId(null);
        setEditingRole("user");
        await refreshUsers();
      } else {
        setMessage("Failed to set user role");
        setMessageType("error");
      }
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      setMessageType("error");
    }
  };

  const handleStartEdit = (user) => {
    setEditingUserId(user.id);
    setEditingRole(user.role || "user");
    setMessage("");
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingRole("user");
  };

  return (
    <>
      <NavBarAdm />
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Admin Panel
        </Typography>

      
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Set User Role
            </Typography>

            {message && (
              <Alert severity={messageType} sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              Use the list below to change roles per user. Click "Change" to edit a user's role inline.
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              All Users
            </Typography>

            <List>
              {allUsers.map((user) => (
                <ListItem
                  key={user.id}
                  divider
                  sx={{ alignItems: "flex-start", py: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    {/* Email + ID (always fully visible, multi-line) */}
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, wordBreak: "break-word", whiteSpace: "normal" }}
                      >
                        {user.email || user.id}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={12}
                        sx={{ wordBreak: "break-word", whiteSpace: "normal", mt: 0.5 }}
                      >
                        User ID: {user.id}
                      </Typography>
                    </Box>

                    {/* Actions moved under the info. on small screens this keeps everything visible;
                        on larger screens actions are right-aligned */}
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                      {editingUserId !== user.id ? (
                        <>
                          <Chip
                            label={`Role: ${user.role || "user"}`}
                            color={
                              user.role === "admin" ? "error" :
                              user.role === "moderator" ? "warning" :
                              user.role === "hofdienst" ? "info" :
                              "default"
                            }
                            variant="filled"
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                          <IconButton
                            size="small"
                            aria-label="edit-role"
                            onClick={() => handleStartEdit(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <TextField
                            select
                            value={editingRole}
                            onChange={(e) => setEditingRole(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            sx={{ minWidth: 140 }}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                            <option value="moderator">Moderator</option>
                            <option value="hofdienst">Hofdienst</option>
                          </TextField>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="save-role"
                            onClick={() => handleSaveRole(user.id)}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="cancel-edit"
                            onClick={handleCancelEdit}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
             </List>
           </CardContent>
         </Card>
       </Container>
     </>
   );
 };
